// ============================================================================
// display.js — show signals as live text in the score
// ----------------------------------------------------------------------------
//   display(v: map(fader.vel, 0, 500, 0, 100), fmt:"speed {v} %", dec:0)
//   display(x: follow(pad.x), y: follow(pad.y))          → "0.42 0.77"
//
// Put it on a <text> element: its content is rewritten in place, so font,
// size and position stay whatever they are in Inkscape. On anything else a
// <text> is added at the host's top-left corner. Every param except fmt/dec/
// uid is a named value; fmt names them as {name}, no fmt joins them with
// spaces. Always on: registered at load, like oscCtrl.
// ============================================================================

import { bindParam } from "../control/paramBinding.js";
import { resolveAnimationUid } from "./animation.js";

const RESERVED = new Set(["fmt", "dec", "uid"]);
const SVG_NS = "http://www.w3.org/2000/svg";

function textTarget(el) {
    if (el.matches?.("text, tspan")) return el.querySelector("tspan") ?? el;
    const inner = el.querySelector?.("tspan, text");
    if (inner) return inner;
    // the one made for it before (a re-registration): not another beside it
    if (el.nextElementSibling?.classList?.contains("oscilla-display")) return el.nextElementSibling;
    let b; try { b = el.getBBox(); } catch { return null; }
    const t = document.createElementNS(SVG_NS, "text");
    t.setAttribute("x", b.x); t.setAttribute("y", b.y);
    t.setAttribute("class", "oscilla-display");
    el.after(t);
    return t;
}

export function handleDisplayCue(el, args = []) {
    const uid = resolveAnimationUid(el, "display", args);
    const target = textTarget(el);
    if (!target) {
        console.error("[display] nowhere to write on", el);
        return;
    }
    const get = (k) => args.find((a) => a.type === k)?.value;
    const fmt = get("fmt") != null ? String(get("fmt")).replace(/^[`'"]+|[`'"]+$/g, "") : null;
    const dec = Number(get("dec") ?? 1);

    const vals = {};
    const render = () => {
        const f = (v) => (Number.isFinite(v) ? v.toFixed(dec) : "");
        target.textContent = fmt
            ? fmt.replace(/\{(\w+)\}/g, (m, k) => (k in vals ? f(vals[k]) : m))
            : Object.values(vals).map(f).join(" ");
    };

    for (const { type: name, value } of args) {
        if (RESERVED.has(name)) continue;
        vals[name] = bindParam(value, (v) => { vals[name] = v; render(); }, { owner: `display:${uid}:${name}` }).value;
    }
    render();
}
